import React, { useEffect, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Row, Col, ListGroup, Image, Card, Toast } from 'react-bootstrap'
import { normalizeImageUrl } from '../utils/urlSanitizer'
import Message from '../components/Message'
import CheckoutSteps from '../components/CheckoutSteps'
import Meta from '../components/Meta'
import StepUpPasswordModal from '../components/StepUpPasswordModal'
import { createOrder, resetOrderState } from '../slices/orderSlice'
import { USER_DETAILS_RESET } from '../constants/userConstants'
import { clearAllCheckout } from '../slices/cartSlice'
import { requestStepUpToken } from '../utils/stepUp'

// Currency formatting (centralized). Change locale/currency here if needed.
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const formatMoney = (v) => currencyFormatter.format(Number.isFinite(+v) ? +v : 0);

const buildAddressLine = (shippingAddress, fallbackFirstName, fallbackLastName) => {
  const fn = (shippingAddress?.shipToFirstName || fallbackFirstName || '').toString().trim()
  const ln = (shippingAddress?.shipToLastName || fallbackLastName || '').toString().trim()
  const fullName = [fn, ln].filter(Boolean).join(' ').trim()

  const parts = [
    fullName,
    (shippingAddress?.address1 || '').toString().trim(),
    (shippingAddress?.address2 || '').toString().trim(),
    (shippingAddress?.city || '').toString().trim(),
    (shippingAddress?.state || '').toString().trim(),
    (shippingAddress?.postalCode || '').toString().trim(),
    (shippingAddress?.country || '').toString().trim(),
  ].filter(Boolean)

  return parts.join(', ')
}

const PlaceOrderScreen = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const cart = useSelector((state) => state.cart)
  const userInfo = useSelector((state) => state.user.userInfo)
  const isGuest = !!userInfo?.isGuest
  const [showOrderToast, setShowOrderToast] = useState(false)
  const [showStepUp, setShowStepUp] = useState(false)
  const [stepUpLoading, setStepUpLoading] = useState(false)
  const [stepUpError, setStepUpError] = useState('')

  const getVariantLabel = (item) => {
    if (!item) return '-'

    const vv = Array.isArray(item.variantValues) ? item.variantValues.filter(Boolean) : []
    if (vv.length) {
      const parts = vv
        .map((x) => {
          const prop = (x?.propName || x?.propId || '').toString().trim()
          const val = (x?.valueName || x?.valueId || '').toString().trim()
          if (prop && val) return `${prop}: ${val}`
          if (val) return val
          return ''
        })
        .filter(Boolean)
      if (parts.length) return parts.join(', ')
    }

    const legacy = (item.selectedColorsVariants || '').toString().trim()
    if (legacy) return legacy

    const sig = (item.variantSignature || '').toString().trim()
    if (sig) return `Variant: ${sig}`

    return '-'
  }

  if (!cart.shippingAddress?.address1) {
    history.push('/shipping')
  }

  // Numeric calculations (store raw numbers, format only at render)
  const itemsPriceNum = cart.cartItems.reduce(
    (acc, item) => acc + (Number(item.price) || 0) * (Number(item.qty) || 0),
    0
  )
  const taxPriceNum = +(itemsPriceNum * 0.15).toFixed(2)
  const shippingPriceNum = Number(cart.selectedRate?.selectedRate) || 0
  const totalPriceNum = +(itemsPriceNum + taxPriceNum + shippingPriceNum).toFixed(2)

  const { order, success, error, loading } = useSelector((state) => state.order)
  const isPlacingOrder = loading === true

  useEffect(() => {
    if (success) {
      dispatch(clearAllCheckout())
      setShowOrderToast(true)
      if (order && order._id) {
        history.push(`/order/${order._id}`)
      }
      dispatch(resetOrderState())
      dispatch({ type: USER_DETAILS_RESET })
    }
  }, [dispatch, history, success, order])

  const buildOrderPayload = (stepUpToken) => ({
    shipToFirstName: cart.shippingAddress?.shipToFirstName,
    shipToLastName: cart.shippingAddress?.shipToLastName,
    firstName: cart.shippingAddress?.shipToFirstName,
    lastName: cart.shippingAddress?.shipToLastName,
    email: cart.shippingAddress?.email,
    orderItems: cart.cartItems,
    shippingAddress: cart.shippingAddress,
    paymentMethod: cart.paymentMethod || 'PayPal',
    itemsPrice: itemsPriceNum,
    shippingPrice: shippingPriceNum,
    taxPrice: taxPriceNum,
    totalPrice: totalPriceNum,
    stepUpToken,
  })

  const placeOrderHandler = () => {
    if (isPlacingOrder) return
    if (error) return
    // Guest checkout is exempt from step-up (guest accounts have no
    // password to re-verify) — place the order directly. Registered
    // shoppers re-confirm their password first.
    if (isGuest) {
      dispatch(createOrder(buildOrderPayload()))
    } else {
      setStepUpError('')
      setShowStepUp(true)
    }
  }

  const handleStepUpConfirm = async (password) => {
    setStepUpLoading(true)
    setStepUpError('')
    try {
      const stepUpToken = await requestStepUpToken(password, userInfo?.token)
      setShowStepUp(false)
      dispatch(createOrder(buildOrderPayload(stepUpToken)))
    } catch (e) {
      setStepUpError(e.message)
    } finally {
      setStepUpLoading(false)
    }
  }

  return (
    <>
  <Meta title='Place Order — lotustor.com' robots='noindex,follow' />
      <CheckoutSteps step1 step2 step3 step4 />
      <Row>
        <Col md={8}>
          <ListGroup variant='flush'>
            <ListGroup.Item>
              <h2>Shipping</h2>
              <p>
                <strong>Address:</strong>{' '}
                {buildAddressLine(cart.shippingAddress, cart.shipToFirstName, cart.shipToLastName)}
              </p>
            </ListGroup.Item>

            <ListGroup.Item>
              <h2>Payment Method</h2>
              <strong>Method: </strong>
              {cart.paymentMethod}
            </ListGroup.Item>

            <ListGroup.Item>
              <h2>Order Items</h2>
              {cart.cartItems.length === 0 ? (
                <Message>Your cart is empty</Message>
              ) : (
                <ListGroup variant='flush'>
                  {cart.cartItems.map((item, index) => (
                    <ListGroup.Item key={index}>
                      <Row>
                        <Col xs="auto">
                          <Image
                            src={normalizeImageUrl(item.image)}
                            alt={item.name}
                            rounded
                            style={{ width: 80, height: 80, objectFit: 'cover' }}
                          />
                        </Col>
                        <Col>
                          <Link to={`/product/${item.product}`}>{item.name}</Link>
                        </Col>
                        <Col>Color/Variation: {getVariantLabel(item)}</Col>
                        <Col md={4}>
                          {item.qty} x {formatMoney(item.price)} = {formatMoney((Number(item.price)||0) * (Number(item.qty)||0))}
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </ListGroup.Item>
          </ListGroup>
        </Col>

        <Col md={4}>
          <Card>
            <ListGroup variant='flush'>
              <ListGroup.Item>
                <h2>Order Summary</h2>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col>Items</Col>
                  <Col>{formatMoney(itemsPriceNum)}</Col>
                </Row>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col>Shipping</Col>
                  <Col>{shippingPriceNum ? formatMoney(shippingPriceNum) : 'FREE'}</Col>
                </Row>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col>Tax</Col>
                  <Col>{formatMoney(taxPriceNum)}</Col>
                </Row>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col>Total</Col>
                  <Col>{formatMoney(totalPriceNum)}</Col>
                </Row>
              </ListGroup.Item>
              {error && (
                <ListGroup.Item>
                  <Message variant='danger'>{error}</Message>
                </ListGroup.Item>
              )}
              <ListGroup.Item>
                <Button
                  type='button'
                  className='btn-block'
                  disabled={isPlacingOrder || cart.cartItems.length === 0}
                  onClick={placeOrderHandler}
                >
                  Place Order
                </Button>
              </ListGroup.Item>
            </ListGroup>
          </Card>
        </Col>
      </Row>

      {/* Toast for order success notification */}
      <Toast
        onClose={() => setShowOrderToast(false)}
        show={showOrderToast}
        delay={3000}
        autohide
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 1050,
        }}
      >
        <Toast.Body>Order placed successfully!</Toast.Body>
      </Toast>

      <StepUpPasswordModal
        show={showStepUp}
        title='Confirm order placement'
        description='For your security, please re-enter your password to place this order.'
        onCancel={() => setShowStepUp(false)}
        onConfirm={handleStepUpConfirm}
        loading={stepUpLoading}
        error={stepUpError}
      />
    </>
  )
}

export default PlaceOrderScreen
